import type { Role } from '../auth/useRole';

/**
 * SP-8a — Menu items flat-list per la top-bar orizzontale.
 *
 * Le voci "platform" (Tenants, Item Schemas, Dialog Templates, Audit global,
 * Voice Config) sono marcate `platform: true`. Header le mostra raggruppate
 * in un dropdown "Piattaforma ▾" quando spazio è limitato (sotto un certo
 * breakpoint) o sempre se l'utente è platform-admin (sono molte).
 *
 * Resto delle voci (Dashboard, Pick-list, Assegnazioni, Live, Magazzini,
 * Utenti, Audit tenant) sono mostrate inline nella top-bar.
 */
export interface MenuItem {
  /** Chiave i18n (formato `namespace:key`, es. `shell:menu.tenants`). */
  label: string;
  /** Bootstrap-icons class (es. `bi-list-check`). */
  icon: string;
  path: string;
  /** Se valorizzato, voce mostrata solo se l'utente ha almeno uno di questi ruoli. */
  roles?: Role[];
  /** SP-8a: voce platform-admin → mostrata nel dropdown "Piattaforma ▾". */
  platform?: boolean;
  disabled?: boolean;
  tooltip?: string;
  /**
   * Voce esclusa dal menu ma con route/codice ancora attivi (raggiungibile
   * via URL diretto). Reversibile: togliere il flag per ri-mostrarla.
   */
  hidden?: boolean;
}

// ─── Voci piatte ────────────────────────────────────────────────────────────

const PLATFORM_ITEMS: MenuItem[] = [
  { label: 'shell:menu.tenants',          icon: 'bi-buildings',        path: '/admin/tenants',          platform: true },
  { label: 'shell:menu.item_schemas',     icon: 'bi-diagram-3',        path: '/admin/item-schemas',     platform: true },
  { label: 'shell:menu.dialog_templates', icon: 'bi-chat-square-dots', path: '/admin/dialog-templates', platform: true },
  { label: 'shell:menu.audit_global',     icon: 'bi-clipboard-data',   path: '/admin/audit',            platform: true },
  { label: 'shell:menu.voice_config',     icon: 'bi-mic',              path: '/voice-config',           platform: true },
];

const MAIN_ITEMS: MenuItem[] = [
  {
    label: 'shell:menu.dashboard',
    icon: 'bi-speedometer2',
    path: '/',
    // voice-supervisor vede la Dashboard (solo "Stato attuale" live, niente
    // card di stato — gestito in Home.tsx).
    roles: ['tenant-admin', 'warehouse-admin', 'voice-supervisor'],
  },
  {
    label: 'shell:menu.picklist',
    icon: 'bi-list-check',
    path: '/pick-lists',
    roles: ['tenant-admin', 'warehouse-admin', 'voice-supervisor'],
  },
  {
    label: 'shell:menu.assignments',
    icon: 'bi-arrows-collapse-vertical',
    path: '/operations/assignments',
    roles: ['warehouse-admin'],
  },
  {
    // "Stato attuale" — contenuto confluito nella Dashboard (Home).
    // Voce nascosta dal menu; route /live resta attiva.
    label: 'shell:menu.live_monitoring',
    icon: 'bi-activity',
    path: '/live',
    roles: ['tenant-admin', 'voice-supervisor', 'warehouse-admin'],
    hidden: true,
  },
  {
    label: 'shell:menu.warehouses',
    icon: 'bi-building',
    path: '/warehouses',
    roles: ['tenant-admin', 'voice-supervisor'],
  },
  {
    label: 'shell:menu.users',
    icon: 'bi-people',
    path: '/users',
    roles: ['tenant-admin', 'voice-supervisor'],
  },
  {
    // "Verifica" — nascosta TEMPORANEAMENTE in fase di sviluppo (da decidere
    // se rimuoverla del tutto). Route /audit e codice restano attivi.
    label: 'shell:menu.audit_tenant',
    icon: 'bi-clipboard-data',
    path: '/audit',
    roles: ['tenant-admin', 'warehouse-admin', 'voice-supervisor'],
    hidden: true,
  },
];

/**
 * Ritorna le voci di menu visibili all'utente in base ai ruoli posseduti.
 * `platform-admin` vede TUTTE le voci `platform: true` + nessuna delle
 * `MAIN_ITEMS` (cross-tenant non ha permessi tenant-specific).
 */
export function menuForRoles(roles: Role[], isPlatformAdmin: boolean): MenuItem[] {
  if (isPlatformAdmin) return PLATFORM_ITEMS.filter((it) => !it.hidden);
  return MAIN_ITEMS.filter(
    (it) => !it.hidden && (!it.roles || it.roles.some((r) => roles.includes(r))),
  );
}

/** Single-role helper per back-compat con callers che hanno il singular role. */
export function menuFor(role: Role | null): MenuItem[] {
  if (role === null) return [];
  if (role === 'platform-admin') return menuForRoles([], true);
  return menuForRoles([role], false);
}
